import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnChanges, SimpleChanges, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { location } from '@pages/vendor/add-candidate/add-candidate.config';
import { GlobalErrorService } from '@services/global-error.service';
import { FilterPersistenceService, SavedCandidateFilterState } from '@services/filter-persistence.service';
import { CandidateFilterService } from '@services/candidate-filter.service';
import { dropdownValues } from '@interfaces/common.model';
import { PersonaType } from '@constants/global.enum';
import { Candidates } from '@interfaces/candidates.model';
import { CANDIDATE_FILTER_CONSTANTS } from '@constants/candidate-filter.constants';
import { CandidateFilterHelpers } from '@helpers/candidate-filter.helpers';

@Component({
  selector: 'app-candidate-filter',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
  ],
  templateUrl: './candidate-filter.component.html',
  styleUrl: './candidate-filter.component.scss',
})
export class CandidateFilterComponent implements OnDestroy, OnChanges {
  @Input()
  fieldsVisibility: string = '';
  @Input() hiringRequestId!: number[];
  @Input() employeeLevel!: number;
  @Input() candidatesData: Candidates[] = [];
  @Output() candidateOutput: EventEmitter<Candidates[]> = new EventEmitter();

  opened: boolean = false;
  filterForm: FormGroup = new FormGroup({});
  personaType = PersonaType;
  locationOptions = location;
  openForm: boolean = true;
  displayData: Candidates[] = [];
  rowData: Candidates[] = [];
  name: dropdownValues[] = [];
  yearsOfExperience: dropdownValues[] = [];
  location: dropdownValues[] = [];
  status: dropdownValues[] = [];
  positions: dropdownValues[] = [];
  hiringManager: dropdownValues[] = [];
  position: dropdownValues[] = [];
  vendor: dropdownValues[] = [];
  isDataLoaded: boolean = false;
  hasSavedFilters: boolean = false;
  formKey: number = 0;
  readonly constants = CANDIDATE_FILTER_CONSTANTS;

  constructor(
    public fb: FormBuilder,
    public globalError: GlobalErrorService,
    private filterPersistenceService: FilterPersistenceService,
    private candidateFilterService: CandidateFilterService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initialzeForm();
    if (this.candidatesData && this.candidatesData.length > 0) {
      this.processCandidates(this.candidatesData);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['candidatesData'] && changes['candidatesData'].currentValue) {
      if (!this.filterForm || Object.keys(this.filterForm.controls).length === 0) {
        this.initialzeForm();
      }
      this.processCandidates(changes['candidatesData'].currentValue);
    }
  }

  ngOnDestroy() {
    if (this.hasFilterValues()) {
      this.saveFilters();
    }
  }

  /* status checkboxes — the control's value may be a plain string array
     (user toggles), option objects (saved-filter restore), or null;
     normalize on read and always write back a string array */
  statusOpen = false;

  get statusSummary(): string {
    const v = this.filterForm?.get(CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_STATUS)?.value;
    let arr: any[] = [];
    if (Array.isArray(v)) arr = v.map((x: any) => (typeof x === 'object' ? x?.value : x)).filter(Boolean);
    else if (v && typeof v === 'object' && v.value) arr = [v.value];
    else if (v) arr = [v];
    if (!arr.length) return 'All';
    if (arr.length <= 2) return arr.join(', ');
    return `${arr.slice(0, 2).join(', ')} +${arr.length - 2} more`;
  }

  isStatusChecked(status: string | number): boolean {
    const v = this.filterForm?.get(CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_STATUS)?.value;
    if (!v) return false;
    if (Array.isArray(v)) return v.some((x: any) => (typeof x === 'object' ? x?.value : x) === status);
    if (typeof v === 'object') return v.value === status;
    return v === status;
  }

  toggleStatus(status: string | number): void {
    const ctrl = this.filterForm?.get(CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_STATUS);
    if (!ctrl) return;
    const v = ctrl.value;
    let arr: (string | number)[] = [];
    if (Array.isArray(v)) arr = v.map((x: any) => (typeof x === 'object' ? x?.value : x)).filter((x: any) => x != null && x !== '');
    else if (v && typeof v === 'object' && v.value) arr = [v.value];
    else if (v) arr = [v];
    arr = arr.includes(status) ? arr.filter((x) => x !== status) : [...arr, status];
    ctrl.setValue(arr.length ? arr : null);
    ctrl.markAsDirty();
  }

  reset() {
    this.filterPersistenceService.clearCandidateFilters(this.fieldsVisibility);
    this.hasSavedFilters = false;
    this.filterForm.reset();
    this.displayData = [...this.rowData];
    this.candidateOutput.emit(this.displayData);
  }

  apply(): void {
    const filterCriteria = this.candidateFilterService.convertFormToFilterCriteria(this.filterForm.value);
    this.saveFilters();

    let excludeFields: string[] = [];
    
    if (this.fieldsVisibility === PersonaType.OpsCandidates) {
      excludeFields = ['jobTitle'];
    } else if (this.fieldsVisibility === PersonaType.hiringMgrCandidates) {
      excludeFields = [];
    } else if (this.fieldsVisibility === PersonaType.vendorCandidates) {
      excludeFields = ['jobTitle'];
    } else {
      excludeFields = [];
    }

    this.displayData = this.candidateFilterService.filterCandidates(this.rowData, filterCriteria, excludeFields);
    this.candidateOutput.emit(this.displayData);
  }

  private processCandidates(data: Candidates[]): void {
    if (!data || data.length === 0) {
      this.candidateOutput.emit([]);
      return;
    }

    this.rowData = data;
    this.displayData = this.rowData;
    const filterOptions = this.candidateFilterService.generateFilterOptions(data);
    this.name = filterOptions.name;
    this.yearsOfExperience = filterOptions.yearsOfExperience;
    this.location = filterOptions.location;
    this.status = filterOptions.status;
    this.hiringManager = filterOptions.hiringManager;
    this.position = filterOptions.position;
    this.vendor = filterOptions.vendor;
    this.cdr.detectChanges();
    this.checkForSavedFilters();    
    setTimeout(() => {
      this.loadSavedFilters();
    }, CANDIDATE_FILTER_CONSTANTS.TIMING.FILTER_RESTORE_DELAY);
  }

  initialzeForm() {
    const formStructure = CandidateFilterHelpers.createFormStructure();
    this.filterForm = this.fb.group(formStructure);
  }

  saveFilters(): void {
    const currentFilters = this.getCurrentCandidateFilterState();
    this.filterPersistenceService.saveCandidateFilters(
      this.fieldsVisibility,
      currentFilters,
    );
    this.hasSavedFilters = true;
    this.cdr.detectChanges();
  }

  checkForSavedFilters(): void {
    const previousValue = this.hasSavedFilters;
    this.hasSavedFilters = this.filterPersistenceService.hasCandidateFilters(
      this.fieldsVisibility,
    );

    if (previousValue !== this.hasSavedFilters) {
      this.cdr.detectChanges();
    }
  }

  loadSavedFilters(): void {
    const savedFilters = this.filterPersistenceService.loadCandidateFilters(
      this.fieldsVisibility,
    );
    if (savedFilters) {
      this.applySavedCandidateFilters(savedFilters);
    } else {
      this.candidateOutput.emit(this.rowData);
      if (this.filterForm) {
        this.filterForm.markAsPristine();
        this.filterForm.updateValueAndValidity();
        this.cdr.detectChanges();
      }
    }
  }

  private hasFilterValues(): boolean {
    if (!this.filterForm) return false;
    
    const filterCriteria = this.candidateFilterService.convertFormToFilterCriteria(this.filterForm.value);
    return this.candidateFilterService.hasFilterValues(filterCriteria);
  }

  private getCurrentCandidateFilterState(): Partial<SavedCandidateFilterState> {
    const formControls = CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS;
    
    return {
      selectedName: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_NAME)?.value),
      selectedExperience: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_EXPERIENCE)?.value),
      selectedLocation: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_LOCATION)?.value),
      selectedStatus: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_STATUS)?.value),
      selectedAvailableBy: CandidateFilterHelpers.convertDateToISOString(this.filterForm.get(formControls.SELECTED_AVAILABLE_BY)?.value),
      selectedDateApplied: CandidateFilterHelpers.convertDateToISOString(this.filterForm.get(formControls.SELECTED_DATE_APPLIED)?.value),
      selectedHiringManager: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_HIRING_MANAGER)?.value),
      selectedPosition: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_POSITION)?.value),
      selectedVendor: CandidateFilterHelpers.extractFormValue(this.filterForm.get(formControls.SELECTED_VENDOR)?.value),
    };
  }

  private applySavedCandidateFilters(savedFilters: SavedCandidateFilterState): void {
    const isDataReady = this.name && this.name.length > 0 && 
                       this.yearsOfExperience && this.yearsOfExperience.length > 0 && 
                       this.status && this.status.length > 0;
                       
    if (!isDataReady) {
      setTimeout(() => this.applySavedCandidateFilters(savedFilters), CANDIDATE_FILTER_CONSTANTS.TIMING.FILTER_RESTORE_DELAY);
      return;
    }

    const patchValue: any = {};
    const nameValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedName);
    if (nameValue && typeof nameValue === 'string') {
      const matchingName = CandidateFilterHelpers.findMatchingOption(this.name, nameValue);
      if (matchingName) {
        patchValue.selectedName = matchingName;
      }
    }

    const experienceValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedExperience);
    if (experienceValue && typeof experienceValue === 'string') {
      const matchingExperience = CandidateFilterHelpers.findMatchingOption(this.yearsOfExperience, experienceValue);
      if (matchingExperience) {
        patchValue.selectedExperience = matchingExperience;
      }
    }

    const locationValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedLocation);
    if (locationValue && typeof locationValue === 'string') {
      const matchingLocation = CandidateFilterHelpers.findMatchingOption(this.locationOptions, locationValue);
      if (matchingLocation) {
        patchValue.selectedLocation = matchingLocation;
      }
    }

    const statusValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedStatus);
    if (statusValue) {
      if (Array.isArray(statusValue)) {
        const matchingStatuses = statusValue.map(val => 
          CandidateFilterHelpers.findMatchingOption(this.status, val)
        ).filter(status => status !== null);
        
        if (matchingStatuses.length > 0) {
          patchValue.selectedStatus = matchingStatuses;
        }
      } else {
        const matchingStatus = CandidateFilterHelpers.findMatchingOption(this.status, statusValue);
        if (matchingStatus) {
          patchValue.selectedStatus = matchingStatus;
        }
      }
    }

    const hiringManagerValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedHiringManager);
    if (hiringManagerValue && typeof hiringManagerValue === 'string') {
      const matchingHiringManager = CandidateFilterHelpers.findMatchingOption(this.hiringManager, hiringManagerValue);
      if (matchingHiringManager) {
        patchValue.selectedHiringManager = matchingHiringManager;
      }
    }

    const vendorValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedVendor);
    if (vendorValue && typeof vendorValue === 'string') {
      const matchingVendor = CandidateFilterHelpers.findMatchingOption(this.vendor, vendorValue);
      if (matchingVendor) {
        patchValue.selectedVendor = matchingVendor;
      }
    }

    const positionValue = CandidateFilterHelpers.extractSavedValue(savedFilters.selectedPosition);
    if (positionValue && typeof positionValue === 'string') {
      const matchingPosition = CandidateFilterHelpers.findMatchingOption(this.position, positionValue);
      if (matchingPosition) {
        patchValue.selectedPosition = matchingPosition;
      }
    }

    const availableByDate = CandidateFilterHelpers.parseSavedDate(savedFilters.selectedAvailableBy);
    if (availableByDate) {
      patchValue.selectedAvailableBy = availableByDate;
    }

    const dateAppliedDate = CandidateFilterHelpers.parseSavedDate(savedFilters.selectedDateApplied);
    if (dateAppliedDate) {
      patchValue.selectedDateApplied = dateAppliedDate;
    }

    const hasFiltersToApply = CandidateFilterHelpers.hasFormValues(patchValue);

    this.filterForm.patchValue(patchValue);
    this.filterForm.markAsDirty();
    this.filterForm.updateValueAndValidity();
    this.cdr.detectChanges();    
    this.openForm = false;
    this.cdr.detectChanges();    
    setTimeout(() => {
      this.openForm = true;
      this.formKey++;
      this.cdr.detectChanges();      
      if (hasFiltersToApply) {
        setTimeout(() => {
          this.apply();
        }, CANDIDATE_FILTER_CONSTANTS.TIMING.FILTER_LOAD_DELAY);
      } else {
        this.candidateOutput.emit(this.rowData);
      }
    }, CANDIDATE_FILTER_CONSTANTS.TIMING.FORM_RESET_DELAY);
  }

  trackByFormKey(): number {
    return this.formKey;
  }
  forceReinitialization(): void {
    if (this.candidatesData && this.candidatesData.length > 0) {
      this.initialzeForm();
      this.processCandidates(this.candidatesData);
    }
  }
}
